// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module RealtimeBadge
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS realtime-visitor badge used by dashboards
 * to surface current activity at a glance. The badge handles loading, empty,
 * and active states without requiring consumers to branch around it.
 */

import type { RealtimeStats } from "@geenius/analytics";
import { Badge, Button } from "@geenius/ui/solidjs";
import { type Component, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";

/**
 * Props for rendering the SolidJS realtime activity badge.
 *
 * @property stats Latest realtime stats snapshot, or `null` when unavailable.
 * @property isLoading Whether the realtime fetch is still pending.
 * @property error Optional realtime fetch error shown as an alert state.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the badge wrapper.
 */
export interface RealtimeBadgeProps {
  stats: RealtimeStats | null;
  isLoading: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  class?: string;
}

function getRealtimeErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Renders a SolidJS badge showing the current active-visitor count.
 *
 * @param props Realtime stats and loading state for the badge.
 * @returns A badge element representing loading, empty, or active realtime state.
 */
export const RealtimeBadge: Component<RealtimeBadgeProps> = (props) => {
  const copy = createAnalyticsCopy();
  const activeLabel = () =>
    props.stats
      ? `${props.stats.activeVisitors} ${copy.t("analytics.realtime.activeNow")}`
      : copy.t("analytics.realtime.activeNow");

  return (
    <Show
      when={!props.error}
      fallback={
        <span
          class={cn(
            "inline-flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive",
            props.class,
          )}
          data-analytics-error-state
          role="alert"
          aria-live="assertive"
        >
          <span aria-hidden="true">!</span>
          <span>{getRealtimeErrorMessage(props.error ?? "")}</span>
          <Show when={props.onRetry}>
            {(onRetry) => (
              <Button
                type="button"
                variant="secondary"
                class="h-6 px-2 text-xs"
                onClick={onRetry()}
              >
                Retry
              </Button>
            )}
          </Show>
        </span>
      }
    >
      <Show
        when={!props.isLoading}
        fallback={
          <Badge
            class={cn("gap-2", props.class)}
            variant="secondary"
            role="status"
            aria-live="polite"
            aria-label="Loading realtime analytics"
          >
            <span data-analytics="realtime-badge" aria-hidden="true">
              ⏳
            </span>
          </Badge>
        }
      >
        <Show
          when={props.stats}
          fallback={
            <Badge
              class={cn("gap-2", props.class)}
              variant="secondary"
              role="status"
              aria-live="polite"
              aria-label="Realtime analytics unavailable"
            >
              <span data-analytics="realtime-badge" aria-hidden="true">
                —
              </span>
            </Badge>
          }
        >
          {(stats) => (
            <Badge
              class={cn("gap-2", props.class)}
              variant={stats().activeVisitors > 0 ? "success" : "secondary"}
              role="status"
              aria-live="polite"
              aria-label={activeLabel()}
            >
              <span data-analytics="realtime-dot" aria-hidden="true">
                🟢
              </span>
              <span data-analytics="realtime-count">
                {stats().activeVisitors}
              </span>
              <span>{copy.t("analytics.realtime.activeNow")}</span>
            </Badge>
          )}
        </Show>
      </Show>
    </Show>
  );
};
